import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { columnsToConfigJson } from './benchmarkViewConfigUtils';

/** Same compact field chrome as AsroTrack ColumnCustomizationPopup. */
const fieldStyle = {
  '& .MuiInputBase-root': {
    height: '1.6rem',
    fontSize: '0.7rem',
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.7rem',
    transform: 'translate(14px, 0.7rem) scale(1)',
  },
  '& .MuiInputLabel-shrink': {
    transform: 'translate(14px, -0.5rem) scale(0.75)',
  },
};

/**
 * AsroTrack-matching Column Customization popup for Benchmark grids.
 * Layout/styling mirrored from asrotrack-frontend ColumnCustomizationPopup.jsx
 * (without asset/workorder accordion sections).
 */
const BenchmarkColumnCustomizationDialog = ({
  open,
  onClose,
  columns,
  category,
  onApply,
  onRestoreDefault,
  onSaveView,
  selectedViewName,
}) => {
  const [localColumns, setLocalColumns] = useState([]);
  const [viewName, setViewName] = useState('My view');

  useEffect(() => {
    if (!open) return;
    setLocalColumns(
      (columns || []).map((c) => ({
        field: c.field,
        headerName: c.headerName || c.field,
        // string while editing so clear/backspace works (Asro parseInt blocked empty)
        width: c.width != null && c.width !== '' ? String(c.width) : '120',
        hide: Boolean(c.hide),
        disableCustomize: c.field === 'hasExtra' || c.field === '_detail',
      }))
    );
    setViewName(selectedViewName || 'My view');
  }, [open, columns, selectedViewName]);

  const handleColumnChange = (index, key, value) => {
    setLocalColumns((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const toApplyPayload = () =>
    localColumns.map((c) => {
      const n = parseInt(String(c.width), 10);
      return {
        field: c.field,
        headerName: c.headerName,
        width: Number.isFinite(n) && n >= 40 ? n : 120,
        hide: Boolean(c.hide),
      };
    });

  const handleApply = () => {
    const payload = toApplyPayload();
    onApply(payload);
    onClose();
  };

  const handleSaveAndApply = () => {
    const payload = toApplyPayload();
    if (onSaveView) {
      onSaveView(viewName, columnsToConfigJson(category, payload, viewName));
    }
    onApply(payload);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '560px',
          maxWidth: 'calc(100% - 32px)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          color: 'text.primary',
          fontWeight: 600,
          fontSize: '1.125rem',
          padding: '16px 20px 12px 20px',
        }}
      >
        {selectedViewName ? `Customize - ${selectedViewName}` : 'Customize Columns'}
      </DialogTitle>

      <DialogContent
        sx={{
          overflowX: 'hidden',
          marginTop: '8px',
          maxHeight: '70vh',
          overflowY: 'auto',
          padding: '8px 20px 16px 20px',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, mt: 1 }}>
          Column Customization
        </Typography>

        {localColumns.map((column, index) => (
          <Box
            key={column.field}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              mb: 0.5,
              gap: 0.5,
            }}
          >
            {/* AsroTrack: ~xs=5 — switch + label */}
            <Box
              sx={{
                flex: '0 0 41%',
                maxWidth: '41%',
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Switch
                checked={!column.hide}
                disabled={column.disableCustomize}
                onChange={(e) =>
                  handleColumnChange(index, 'hide', !e.target.checked)
                }
                size="small"
              />
              <Typography
                sx={{ fontSize: '0.7rem' }}
                display="inline"
                noWrap
                title={column.headerName}
              >
                {column.headerName}
              </Typography>
            </Box>

            {/* AsroTrack: ~xs=4 — Header Name */}
            <Box sx={{ flex: '1 1 33%', minWidth: 0, pr: 1 }}>
              <TextField
                sx={fieldStyle}
                label="Header Name"
                fullWidth
                value={column.headerName}
                onChange={(e) =>
                  handleColumnChange(index, 'headerName', e.target.value)
                }
                disabled={column.hide || column.disableCustomize}
              />
            </Box>

            {/* AsroTrack: ~xs=3 — Width */}
            <Box sx={{ flex: '0 0 80px' }}>
              <TextField
                sx={fieldStyle}
                label="Width"
                type="text"
                inputMode="numeric"
                value={column.width}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || /^\d*$/.test(raw)) {
                    handleColumnChange(index, 'width', raw);
                  }
                }}
                disabled={column.hide || column.disableCustomize}
                style={{ width: 80 }}
              />
            </Box>
          </Box>
        ))}

        <Box sx={{ mt: 2 }}>
          <TextField
            sx={fieldStyle}
            label="Save as view name"
            fullWidth
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: '12px 20px',
          justifyContent: 'flex-end',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        {onRestoreDefault && (
          <Button
            onClick={onRestoreDefault}
            sx={{
              textTransform: 'none',
              mr: 'auto',
              fontSize: '0.875rem',
            }}
          >
            Restore Default
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            px: 2.5,
            py: 0.75,
            fontSize: '0.875rem',
          }}
        >
          Cancel
        </Button>
        {onSaveView && (
          <Button
            variant="outlined"
            onClick={handleSaveAndApply}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              px: 2.5,
              py: 0.75,
              fontSize: '0.875rem',
            }}
          >
            Save View
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            px: 2.5,
            py: 0.75,
            fontSize: '0.875rem',
          }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BenchmarkColumnCustomizationDialog;
